import { useEffect, useState } from 'react';
import { useOrganisation, useSettingsMutations } from '../../../hooks/useSettings.js';
import { useAuth } from '../../../hooks/useAuth.jsx';
import { ApiError } from '../../../lib/api.js';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog.jsx';

/**
 * OrganisationPage — rename organisation, change slug, contact email.
 *
 * Slug changes are disruptive — they break printed QR codes. We prompt
 * the user with a warning before accepting the change and mention the
 * grace period (Randy's backend will honour old slug for N days via
 * redirect).
 */

export function OrganisationPage() {
  const { data: org, loading, error, refresh } = useOrganisation();
  const { refresh: refreshAuth } = useAuth();
  const { updateOrganisation } = useSettingsMutations();

  const [form, setForm] = useState({ name: '', slug: '', public_contact_email: '' });
  const [originalSlug, setOriginalSlug] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [slugConfirmOpen, setSlugConfirmOpen] = useState(false);

  useEffect(() => {
    if (org) {
      setForm({
        name: org.name || '',
        slug: org.slug || '',
        public_contact_email: org.public_contact_email || '',
      });
      setOriginalSlug(org.slug || '');
      setDirty(false);
    }
  }, [org]);

  function setField(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    setDirty(true);
    setSaveSuccess(false);
  }

  function handleSubmit(e) {
    e.preventDefault();
    setSaveError(null);

    // If slug changed, warn before committing
    if (form.slug !== originalSlug) {
      setSlugConfirmOpen(true);
      return;
    }

    doSave();
  }

  async function doSave() {
    setSlugConfirmOpen(false);
    setSaving(true);
    try {
      await updateOrganisation({
        name: form.name.trim(),
        slug: form.slug.trim(),
        public_contact_email: form.public_contact_email.trim() || null,
      });
      setDirty(false);
      setSaveSuccess(true);
      setOriginalSlug(form.slug.trim());
      refresh();
      // Auth context carries tenant.name — refresh so topbar updates
      refreshAuth?.();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Loading />;
  if (error) return <LoadError onRetry={refresh} />;

  return (
    <>
      {saveError && <div className="alert alert-error" role="alert">{saveError}</div>}
      {saveSuccess && (
        <div className="alert alert-success" role="status">
          Organisation settings saved.
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="settings-card">
          <h2 className="settings-card-title">Organisation name</h2>
          <p className="settings-card-body">
            Shown on verify pages and ID cards. This is what members of
            the public see when they scan a staff QR.
          </p>
          <div className="field">
            <input
              id="f-name"
              className="field-input"
              type="text"
              value={form.name}
              onChange={e => setField('name', e.target.value)}
              required
              maxLength={120}
              aria-label="Organisation name"
            />
          </div>
        </div>

        <div className="settings-card">
          <h2 className="settings-card-title">Tenant slug</h2>
          <p className="settings-card-body">
            Part of your public verify URL:{' '}
            <code style={{ fontFamily: 'var(--font-mono)', background: 'var(--cream)', padding: '2px 6px', borderRadius: 4 }}>
              tocynid.com/v/{form.slug || 'your-slug'}/...
            </code>
            . Lowercase letters, numbers, hyphens only.{' '}
            <strong>Changing this will invalidate all existing printed QR codes.</strong>{' '}
            Old URLs redirect for 90 days, then permanently expire.
          </p>
          <div className="field">
            <input
              id="f-slug"
              className="field-input"
              type="text"
              value={form.slug}
              onChange={e => setField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              pattern="[a-z0-9-]+"
              required
              maxLength={60}
              aria-label="Tenant slug"
              style={{ fontFamily: 'var(--font-mono)' }}
            />
          </div>
        </div>

        <div className="settings-card">
          <h2 className="settings-card-title">Public contact email</h2>
          <p className="settings-card-body">
            Optional — shown on verify pages so members of the public
            can contact you if they have questions about a staff
            member. Leave blank to hide.
          </p>
          <div className="field">
            <input
              id="f-contact-email"
              className="field-input"
              type="email"
              value={form.public_contact_email}
              onChange={e => setField('public_contact_email', e.target.value)}
              placeholder="hello@yourorganisation.co.uk"
              maxLength={200}
              aria-label="Public contact email"
            />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-3)' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving || !dirty}
            aria-busy={saving}
          >
            {saving ? (<><span className="spinner" aria-hidden="true" /> Saving…</>) : 'Save changes'}
          </button>
        </div>
      </form>

      <ConfirmDialog
        open={slugConfirmOpen}
        title="Change tenant slug?"
        body={
          <>
            Changing your slug from <strong>{originalSlug}</strong> to{' '}
            <strong>{form.slug}</strong> will break all existing printed
            QR codes.{' '}
            <br /><br />
            Old QR codes will redirect to the new URL for 90 days, then
            stop working entirely. You'll need to reprint all staff cards.{' '}
            <br /><br />
            Are you sure?
          </>
        }
        confirmLabel="Change slug"
        destructive
        submitting={saving}
        onConfirm={doSave}
        onCancel={() => setSlugConfirmOpen(false)}
      />
    </>
  );
}

function Loading() {
  return (
    <div style={{ padding: 'var(--sp-10) 0', textAlign: 'center' }}>
      <div className="spinner" aria-hidden="true" />
    </div>
  );
}

function LoadError({ onRetry }) {
  return (
    <div className="alert alert-error" role="alert">
      <strong>Couldn't load organisation settings.</strong>{' '}
      <button type="button" className="btn btn-ghost" onClick={onRetry} style={{ padding: 0, textDecoration: 'underline' }}>
        Try again
      </button>
    </div>
  );
}
